import React, { useEffect, useState } from 'react';
import ReactBreakpoints from '@kaunio/react-breakpoints';
import { BrowserRouter as Router, Route } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Box, ThemeProvider } from '@mui/material';
import Wrapper from './Wrapper';
import { breakpointProviderProps } from '../../constants/DeviceBreakpoints';
import { strapiClient } from '../../core/api/strapi-client';
import { getSiteConfig } from '../../core/api/meilisearch-client';
import { regionStore } from '../../store/jotai/store';
import { siteConfigAtom } from '../../store/jotai/atoms/site-config.atom';
import { theme } from '../../theme/theme';
import { userAtom } from '../../store/jotai/atoms/user.atom';
import { authAtom } from '../../store/jotai/atoms/auth.atom';
import { basketService } from '../../services/basket-service';
import { WEBSHOP_ENABLED } from '../../constants/Settings';
import { Loader } from '../../components/Loader/Loader';
import { shippingRateService } from '../../services/shipping-rate-service';
import { Logger } from '../../core/logger/Logger';
import { captureException } from '../../utils/sentry';
import { initSocket } from '../../core/api/socket-client';
import { chatService } from '../../services/chat-service';
import { geolocationService } from '../../services/geolocation-service';
import { versionService } from '../../services/version-service';
import { NewVersionPrompt } from '../../components/NewVersionPrompt/NewVersionPrompt';

const Root = () => {
  const [initialAppLoading, setInitialAppLoading] = useState(true);

  useEffect(() => {
    // Notices when a newer build is being served, so a tab holding a stale
    // bundle can be reloaded on purpose instead of failing in confusing ways.
    const stopVersionChecks = versionService.start();

    geolocationService.startWatching();

    regionStore.set(authAtom, 'loading');

    const initialize = async () => {
      // The user and the site config are independent reads — fetch them
      // concurrently so first paint waits on one round trip, not two.
      // Each settles on its own so a failure of one still boots the app.
      const [, siteConfig] = await Promise.all([
        strapiClient.loadUser({ skipStripe: !WEBSHOP_ENABLED }).catch((error) => {
          Logger.error('Failed to load user:', error);
          captureException(error, { context: 'Root.loadUser' });
        }),
        getSiteConfig(1).catch((error) => {
          Logger.error('Failed to load site config:', error);
          captureException(error, { context: 'Root.getSiteConfig' });
          return null;
        })
      ]);

      if (siteConfig) {
        regionStore.set(siteConfigAtom, siteConfig);
      }

      const user = regionStore.get(userAtom);
      regionStore.set(authAtom, user ? 'authenticated' : 'unauthenticated');

      if (user) {
        if (WEBSHOP_ENABLED) {
          basketService.getBasket().catch(() => {});
        }
        chatService.getConversations().catch(() => {});

        // Auth travels via the httpOnly session cookie (withCredentials).
        try {
          initSocket();
        } catch (error) {
          Logger.error('Socket init error:', error);
          captureException(error, { context: 'Root.socketInit' });
        }
      }

      if (WEBSHOP_ENABLED && user?.vendors?.length) {
        shippingRateService.getShippingRates().catch(() => {});
      }

      setInitialAppLoading(false);
    };

    initialize();

    return () => {
      geolocationService.stopWatching();
      stopVersionChecks();
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <ReactBreakpoints {...breakpointProviderProps}>
        <div className='root-container'>
          <AnimatePresence>
            {initialAppLoading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}>
                <Box sx={{ width: '100vw', height: '100vh' }}>
                  <Loader text='' overlay={true} />
                </Box>
              </motion.div>
            )}
            {!initialAppLoading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}>
                <Router>
                  <Route path='/' component={Wrapper} />
                </Router>
              </motion.div>
            )}
          </AnimatePresence>
          <NewVersionPrompt />
        </div>
      </ReactBreakpoints>
    </ThemeProvider>
  );
};

export default Root;
